/**
 * InertEmbedPlaceholder — stands in for a HubSpot-hosted embed on staging.
 *
 * Rendered only when HUBSPOT_EMBEDS_DISABLED is set (see
 * src/lib/hubspot-embed-gate.ts). Deliberately plain and obviously
 * non-production: the point is that a tester immediately understands why the
 * form/booking widget is missing instead of assuming staging is broken.
 *
 * Server component — safe to render from both server dispatchers (EmbedSlot)
 * and client leaves (HubSpotForm, HubSpotMeetingsLoader).
 */

interface InertEmbedPlaceholderProps {
  /** What would render here in production, e.g. "HubSpot booking calendar". */
  label: string;
  /** The prod-portal resource this stands in for, shown for debugging. */
  detail?: string;
  className?: string;
  minHeight?: string;
}

export function InertEmbedPlaceholder({
  label,
  detail,
  className,
  minHeight = '160px',
}: InertEmbedPlaceholderProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center gap-1 rounded border border-dashed border-gray-400 bg-gray-50 p-6 text-center ${className ?? ''}`.trim()}
      style={{ minHeight }}
      data-hubspot-embed-disabled="true"
      role="note"
    >
      <p className="text-sm font-semibold text-gray-700">{label} disabled on staging</p>
      <p className="text-xs text-gray-500">
        This embed is hosted on the production HubSpot portal and would write to the live CRM.
      </p>
      {detail ? <p className="mt-1 font-mono text-[11px] text-gray-400">{detail}</p> : null}
    </div>
  );
}
